import { EventBarrier } from "@/components/common/event-barrier";
import { selectRegistrationRevision } from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { selectClusterArray } from "@/store/ielements-slice-selector";
import { selectHasChangesInDraft } from "@/store/revisions-selector";
import { useAppSelector } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import { useNavigateToDraftView } from "@/utils/redirects";
import { blue, FaroButton } from "@faro-lotv/flat-ui";
import { selectProjectId } from "@faro-lotv/project-source";
import { isScanEntity } from "@faro-lotv/service-wires";
import { Badge } from "@mui/material";
import { Box } from "@mui/system";
import type { JSX } from "react";

/**
 * @returns "Edit scans" button which redirects to Draft View.
 * It's only visible if the user has write permissions and there are scans in the project or
 * there's an open draft revision.
 * If there are changes in the open draft revision, a dot indicator is shown in the button.
 */
export function EditScansButton(): JSX.Element | null {
  const navigateToDraftView = useNavigateToDraftView();
  const projectId = useAppSelector(selectProjectId);
  const hasWritePermission = useAppSelector(selectHasWritePermission);
  const clusters = useAppSelector(selectClusterArray);
  const revision = useAppSelector(selectRegistrationRevision);
  const hasChangesInDraft = useAppSelector(selectHasChangesInDraft);

  const hasScans = clusters.some((cluster) => isScanEntity(cluster));
  const isVisible =
    !!projectId && hasWritePermission && (hasScans || !!revision);

  if (!isVisible) {
    return null;
  }

  return (
    <EventBarrier>
      <Box
        component="div"
        sx={{
          mx: 2,
          // No bottom margin since there's already a top margin by the import button below.
          mt: 1,
        }}
      >
        <FaroButton
          aria-label="edit scans"
          variant="secondary"
          sx={{ width: "100%" }}
          onClick={() => {
            navigateToDraftView(projectId, "editScans");
          }}
        >
          {hasChangesInDraft ? (
            // Give the indicator dot the same style as the one in the View/Edit toggle button (FaroToggleGroup).
            <Badge
              variant="dot"
              color="primary"
              sx={{
                pr: 0.75,
                "& .MuiBadge-dot": {
                  minWidth: "6px",
                  width: "6px",
                  minHeight: "6px",
                  height: "6px",
                  backgroundColor: blue[400],
                  border: "none",
                },
              }}
            >
              Edit scans
            </Badge>
          ) : (
            <span>Edit scans</span>
          )}
        </FaroButton>
      </Box>
    </EventBarrier>
  );
}
